export interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  tags?: string[];
  placement?: 'top' | 'all';
  ctas?: {
    label: string;
    to: string;
    event: string;
  }[];
}

// Single source of truth for all FAQ content
// Used by both /faqs page and DomainFAQ component
export const FAQ_DATA: FAQItem[] = [
  // ========== Top Objections (pinned, shows first) ==========
  {
    id: 'obj-lease-vs-later',
    category: 'Top Objections',
    placement: 'top',
    question: 'Why lease a domain instead of buying one "later"?',
    answer:
      "Speed + cash flow. Leasing gets you live in minutes and protects your place—no giant cash outlay. You can still buy at the posted BIN any time. Every domain is one-of-one; if someone else buys {DOMAIN}, it's gone.",
    tags: ['lease', 'timing', 'scarcity'],
    ctas: [{ label: 'Start Lease in Minutes', to: '/lease', event: 'faq_cta_lease_now' }],
  },
  {
    id: 'obj-worth-it',
    category: 'Top Objections',
    placement: 'top',
    question: 'What if the domain isn\'t "worth it" compared to a cheaper name?',
    answer:
      'Cheap names cost you in lost trust and lower clicks. Memorable, on-brand domains lift conversion and ad efficiency. A single client can repay the Lease many times over—losing that client costs more than the monthly fee.',
    tags: ['value', 'roi', 'trust'],
  },
  {
    id: 'obj-lease-credit',
    category: 'Top Objections',
    placement: 'top',
    question: 'Do Lease payments reduce the BIN price later?',
    answer:
      'By default, no—Lease is pay-for-use. Sometimes we run short promos with limited credit; if active, it will be clearly labeled.',
    tags: ['lease', 'pricing', 'credit'],
  },
  {
    id: 'obj-payment-safety',
    category: 'Top Objections',
    placement: 'top',
    question: 'Is payment safe—and what exactly happens on transfer or cancel?',
    answer:
      'Payments run through our trusted payment provider for BIN and secure billing for Lease. For BIN, transfer occurs only after secured funds; for Lease, you can cancel anytime (license ends; you should repoint DNS before canceling).',
    tags: ['safety', 'escrow', 'transfer'],
  },
  {
    id: 'obj-need-developer',
    category: 'Top Objections',
    placement: 'top',
    question: "I don't have a developer. Can you build it?",
    answer:
      'We focus on domains. If you need help, see our vetted partners who build service sites fast.',
    tags: ['services', 'development', 'partners'],
    ctas: [{ label: 'See Vetted Builders', to: '/partners', event: 'faq_cta_partners' }],
  },

  // ========== Leasing (Use It Monthly) ==========
  {
    id: 'lease-how-it-works',
    category: 'Leasing (Use It Monthly)',
    placement: 'top',
    question: 'How does "Use It Monthly" work?',
    answer:
      "It's a month-to-month license for {DOMAIN}. Start for {LEASE_PER_MONTH} (~{LEASE_PER_DAY}/day), stop anytime, and you may purchase at {BIN_PRICE} whenever you're ready.",
    tags: ['lease', 'basics', 'pricing'],
  },
  {
    id: 'lease-protection',
    category: 'Leasing (Use It Monthly)',
    placement: 'top',
    question: "Can someone else buy the domain while I'm leasing?",
    answer:
      "No—while your Lease is active and in good standing, your license is protected. If you cancel and the name sells later, it's no longer available.",
    tags: ['lease', 'protection', 'scarcity'],
  },
  {
    id: 'lease-payment-fail',
    category: 'Leasing (Use It Monthly)',
    question: 'What happens if a payment fails or I miss a month?',
    answer:
      "We'll email you to update your card. If unpaid past the grace period, the license suspends and may terminate. Keep your DNS plan ready before canceling or missing payments.",
    tags: ['lease', 'billing', 'cancellation'],
  },
  {
    id: 'lease-services',
    category: 'Leasing (Use It Monthly)',
    question: 'Are services (design, SEO, email) included?',
    answer: 'No—services are separate. Use your own vendor or pick from our partners.',
    tags: ['lease', 'services'],
    ctas: [{ label: 'Find a Partner', to: '/partners', event: 'faq_cta_partners' }],
  },
  {
    id: 'lease-dns',
    category: 'Leasing (Use It Monthly)',
    question: 'Can I point DNS and fully use email/SSL?',
    answer:
      "Yes—standard DNS pointing is supported for live use during an active Lease. You're responsible for site/email configuration.",
    tags: ['lease', 'technical', 'dns'],
  },

  // ========== Buying (BIN) ==========
  {
    id: 'bin-whats-included',
    category: 'Buying (BIN)',
    question: "What's included with BIN (Buy It Now)?",
    answer:
      'The domain only. Funds are protected by our trusted payment provider; transfer (push or auth code) happens after payment clears. No services are bundled unless separately purchased.',
    tags: ['bin', 'purchase', 'transfer'],
  },
  {
    id: 'bin-firm-price',
    category: 'Buying (BIN)',
    question: 'Why is the BIN price firm?',
    answer:
      'We keep price parity across marketplaces to avoid confusion. Serious buyers use Make-Offer for structured negotiation above a minimum.',
    tags: ['bin', 'pricing', 'negotiation'],
  },

  // ========== Offers & Negotiation ==========
  {
    id: 'offer-below-bin',
    category: 'Offers & Negotiation',
    question: 'Can I make an offer below BIN?',
    answer:
      'Yes—use "Make an Offer." Our form blocks lowballs below {FLOOR_PCT}% of BIN. Tell us your intended use and timeline for a fast decision.',
    tags: ['offers', 'negotiation', 'pricing'],
    ctas: [{ label: 'Make a Serious Offer', to: '#make-offer', event: 'faq_cta_make_offer' }],
  },
  {
    id: 'offer-po-invoice',
    category: 'Offers & Negotiation',
    question: 'I need a PO, invoice, or NDA—can you accommodate?',
    answer:
      'Yes—pro-forma or paid invoices, and a simple mutual NDA on request. Mention this in your offer notes.',
    tags: ['offers', 'enterprise', 'legal'],
  },

  // ========== "Get It Free" (80/20, apply only) ==========
  {
    id: 'operator-what-is',
    category: '"Get It Free" (80/20, apply only)',
    question: 'What is the 80/20 operator option?',
    answer:
      'You build and run the site; we keep ownership. Strict approval, clear milestones, and brand-safe rules apply. You can buy at BIN later if you choose.',
    tags: ['operator', '80/20', 'free'],
    ctas: [{ label: 'Apply to Operate', to: '/operate', event: 'faq_cta_apply_operator' }],
  },
  {
    id: 'operator-qualify',
    category: '"Get It Free" (80/20, apply only)',
    question: 'Who qualifies for 80/20?',
    answer:
      'Builders with a plan, proof of execution, and a 90-day timeline. We select a small cohort per quarter to keep quality high.',
    tags: ['operator', 'qualification', 'approval'],
  },

  // ========== General & Risk ==========
  {
    id: 'general-clean-domain',
    category: 'General & Risk',
    question: 'Is this domain "clean" (no penalties or spam history)?',
    answer:
      'To the best of our knowledge, yes. You should do your own checks (analytics, Search Console after pointing). Prohibited uses: spam, deception, illegal content, or brand harm.',
    tags: ['general', 'safety', 'compliance'],
  },
  {
    id: 'general-trademark',
    category: 'General & Risk',
    question: 'Do you provide trademark clearance?',
    answer:
      'No—buyers are responsible for TM checks and compliant use. We provide links to free TM search tools.',
    tags: ['general', 'legal', 'trademark'],
  },

  // ========== After Purchase ==========
  {
    id: 'after-transfer-timeline',
    category: 'After Purchase',
    question: 'How long does it take before I can use my new domain?',
    answer:
      'For BIN purchases, you\'ll receive your EPP (authorization) code within 24-48 hours. Once you initiate the transfer at your registrar, the process typically takes 5-7 days. For LTO, DNS access is available immediately after your first payment.',
    tags: ['transfer', 'timeline', 'dns', 'bin', 'lease'],
  },
  {
    id: 'after-dns-access',
    category: 'After Purchase',
    question: 'How will I get access to DNS for my leased domain?',
    answer:
      'DNS access is granted immediately after your first lease payment. You\'ll receive an email with DNS configuration details. You can point your domain to your website, email provider, or hosting service right away.',
    tags: ['lease', 'dns', 'access'],
  },
  {
    id: 'after-refunds',
    category: 'After Purchase',
    question: 'Do you offer refunds?',
    answer:
      'Refund policies vary by purchase type. For BIN purchases, refunds are handled on a case-by-case basis per our Terms of Service. For LTO, you can cancel your lease at any time (no refunds for payments already made). See our Refund Policy for details.',
    tags: ['refunds', 'policy'],
    ctas: [{ label: 'View Refund Policy', to: '/refund', event: 'faq_cta_refund_policy' }],
  },
  {
    id: 'lto-early-payoff',
    category: 'Lease-to-Own',
    question: 'Can I pay off my Lease-to-Own early?',
    answer:
      'Yes! You can pay off your lease early at any time. Contact support to request an early payoff link, or use the "Buy Now" option in your account dashboard if available. Early payoff transfers full ownership immediately.',
    tags: ['lease', 'early-payoff', 'ownership'],
  },
  {
    id: 'after-invoice-receipts',
    category: 'After Purchase',
    question: 'Where can I find my invoices and receipts?',
    answer:
      'You\'ll receive email receipts from Stripe for all payments. You can also view your payment history and download invoices through your account dashboard or the Stripe Customer Portal (accessible via "Manage Billing" in your account).',
    tags: ['billing', 'invoices', 'receipts'],
  },
];

// Helper function to get FAQs by placement
export const getFAQsByPlacement = (placement: 'top' | 'all' = 'all'): FAQItem[] => {
  if (placement === 'top') {
    return FAQ_DATA.filter((faq) => faq.placement === 'top');
  }
  return FAQ_DATA;
};

// Helper function to get FAQs by category
export const getFAQsByCategory = (category: string): FAQItem[] => {
  return FAQ_DATA.filter((faq) => faq.category === category);
};

// Get all unique categories in order
export const FAQ_CATEGORIES = [
  'Top Objections',
  'Leasing (Use It Monthly)',
  'Lease-to-Own',
  'Buying (BIN)',
  'After Purchase',
  'Offers & Negotiation',
  '"Get It Free" (80/20, apply only)',
  'General & Risk',
];

// Helper to replace dynamic variables in answers
export const replaceFAQVariables = (
  text: string,
  variables: {
    domain?: string;
    binPrice?: string;
    leasePerMonth?: string;
    leasePerDay?: string;
    floorPct?: string;
  },
): string => {
  return text
    .replace(/\{DOMAIN\}/g, variables.domain || '[domain]')
    .replace(/\{BIN_PRICE\}/g, variables.binPrice || '$X,XXX')
    .replace(/\{LEASE_PER_MONTH\}/g, variables.leasePerMonth || '$XXX/mo')
    .replace(/\{LEASE_PER_DAY\}/g, variables.leasePerDay || '$X/day')
    .replace(/\{FLOOR_PCT\}/g, variables.floorPct || 'X');
};
